import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  searchPharmacies,
  clearPharmacySearch,
} from '../../store/RxntSlice';
import FormInputs from '../UI/FormInputs';
import {
  emptyPharmacyFilters,
  formatPharmacyAddress,
  getPharmacyPhone,
  pharmacyFiltersFromGatewayPharmacy,
} from '../../utils/rxntUiUtils';

const RxntPharmacyPicker = ({
  selectedPharmacy,
  initialFilters,
  filtersSyncKey,
  onSelect,
  onClear,
  showRequiredBadge = false,
  idPrefix = 'rxnt_pharmacy',
  title = 'Pharmacy',
  Toast,
}) => {
  const dispatch = useDispatch();
  const { pharmacySearch } = useSelector((state) => state.rxnt);
  const stateList = useSelector((state) => state.session.profileStateList || []);

  const [pharmacyFilters, setPharmacyFilters] = useState(
    initialFilters ? { ...emptyPharmacyFilters, ...initialFilters } : { ...emptyPharmacyFilters }
  );
  const [activePharmacyQuery, setActivePharmacyQuery] = useState(null);
  const [showPharmacyDropdown, setShowPharmacyDropdown] = useState(false);
  const prevFiltersSyncKeyRef = useRef(undefined);

  useEffect(() => {
    const syncKey =
      filtersSyncKey != null && filtersSyncKey !== '' ? String(filtersSyncKey) : '';

    if (!syncKey) {
      if (prevFiltersSyncKeyRef.current !== syncKey) {
        prevFiltersSyncKeyRef.current = syncKey;
        if (!selectedPharmacy) {
          setPharmacyFilters({ ...emptyPharmacyFilters });
        }
      }
      return;
    }

    const compositeKey = `${syncKey}|${JSON.stringify(initialFilters || {})}`;
    if (prevFiltersSyncKeyRef.current === compositeKey) {
      return;
    }
    prevFiltersSyncKeyRef.current = compositeKey;

    if (initialFilters) {
      setPharmacyFilters({ ...emptyPharmacyFilters, ...initialFilters });
    }
  }, [filtersSyncKey, initialFilters, selectedPharmacy]);

  useEffect(() => {
    return () => {
      dispatch(clearPharmacySearch());
    };
  }, [dispatch]);

  const buildPharmacySearchQuery = useCallback((filters) => {
    const normalized = {
      name: (filters.name || '').trim(),
      address: (filters.address || '').trim(),
      city: (filters.city || '').trim(),
      state: (filters.state || '').trim().slice(0, 2).toUpperCase(),
      zip: (filters.zip || '').trim().slice(0, 10),
      phone: (filters.phone || '').replace(/\D/g, '').slice(0, 10),
      fax: (filters.fax || '').replace(/\D/g, '').slice(0, 10),
    };

    if (normalized.name && normalized.name.length < 2) {
      return { error: 'Pharmacy name must be at least 2 characters' };
    }

    const hasAny = Object.values(normalized).some(Boolean);
    if (!hasAny) {
      return { error: 'Enter at least one pharmacy filter' };
    }

    return { query: normalized };
  }, []);

  const triggerPharmacySearch = useCallback(
    (page = 1, append = false, baseQuery = null) => {
      const source = baseQuery || pharmacyFilters;
      const built = buildPharmacySearchQuery(source);
      if (built.error) {
        if (!append && typeof window !== 'undefined') {
          // eslint-disable-next-line no-alert
          console.warn(built.error);
        }
        return;
      }

      const query = built.query;
      setActivePharmacyQuery(query);
      setShowPharmacyDropdown(true);
      dispatch(searchPharmacies({ ...query, page, append }));
    },
    [pharmacyFilters, buildPharmacySearchQuery, dispatch]
  );

  const handlePharmacyDropdownScroll = useCallback(
    (e) => {
      const el = e.currentTarget;
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 72;
      if (!nearBottom) return;
      if (
        !pharmacySearch.hasNext ||
        pharmacySearch.loadingMore ||
        pharmacySearch.loading
      ) {
        return;
      }
      if (!activePharmacyQuery) return;
      dispatch(
        searchPharmacies({
          ...activePharmacyQuery,
          page: (pharmacySearch.page || 1) + 1,
          append: true,
        })
      );
    },
    [
      pharmacySearch.hasNext,
      pharmacySearch.loadingMore,
      pharmacySearch.loading,
      pharmacySearch.page,
      activePharmacyQuery,
      dispatch,
    ]
  );

  const handlePharmacyFilterChange = (key, rawValue) => {
    let value = rawValue;
    if (key === 'state') {
      value = String(rawValue ?? '').toUpperCase().slice(0, 2);
    } else if (key === 'phone' || key === 'fax') {
      value = String(rawValue ?? '').replace(/\D/g, '').slice(0, 10);
    }
    setPharmacyFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handlePharmacySearchClear = useCallback(() => {
    setPharmacyFilters({ ...emptyPharmacyFilters });
    setActivePharmacyQuery(null);
    setShowPharmacyDropdown(false);
    dispatch(clearPharmacySearch());
  }, [dispatch]);

  const handlePharmacySelect = (pharmacy) => {
    setPharmacyFilters(pharmacyFiltersFromGatewayPharmacy(pharmacy));
    setShowPharmacyDropdown(false);
    dispatch(clearPharmacySearch());
    if (onSelect) onSelect(pharmacy);
  };

  const handleRemoveSelected = () => {
    setPharmacyFilters({ ...emptyPharmacyFilters });
    setActivePharmacyQuery(null);
    if (onClear) onClear();
  };

  const runSearch = () => {
    const built = buildPharmacySearchQuery(pharmacyFilters);
    if (built.error) {
      if (Toast?.warning) {
        Toast.warning(built.error);
      }
      return;
    }
    triggerPharmacySearch(1, false);
  };

  return (
    <div className="card mb-3 rxnt-form-section-card rxnt-pharmacy-card">
      <div className="card-header rxnt-pharmacy-card-header d-flex flex-column flex-sm-row justify-content-between align-items-start gap-2 p-3">
        <div>
          <h6 className="mb-1 text-md font-weight-bold">{title}</h6>
        </div>
        {showRequiredBadge && (
          <span className="rxnt-pharmacy-required-badge">Required for send</span>
        )}
      </div>
      <div className="card-body p-3">
        <div className="rxnt-pharmacy-search-panel">
          <div className="rxnt-pharmacy-panel-title text-uppercase small fw-semibold text-muted mb-3">
            Search criteria
          </div>
          <div className="row g-3">
            <div className="col-12 col-lg-6">
              <label className="rxnt-pharmacy-field-label mb-1 d-block" htmlFor={`${idPrefix}_name`}>
                Pharmacy name
              </label>
              <input
                id={`${idPrefix}_name`}
                type="text"
                className="form-control form-control-solid"
                value={pharmacyFilters.name}
                onChange={(e) => handlePharmacyFilterChange('name', e.target.value)}
                placeholder="e.g. CVS, Walgreens"
                autoComplete="off"
              />
            </div>
            <div className="col-12 col-lg-6">
              <label className="rxnt-pharmacy-field-label mb-1 d-block" htmlFor={`${idPrefix}_address`}>
                Address
              </label>
              <input
                id={`${idPrefix}_address`}
                type="text"
                className="form-control form-control-solid"
                value={pharmacyFilters.address}
                onChange={(e) => handlePharmacyFilterChange('address', e.target.value)}
                placeholder="Street"
                autoComplete="off"
              />
            </div>
          </div>
          <div className="row g-3 mt-0">
            <div className="col-12 col-md-4">
              <label className="rxnt-pharmacy-field-label mb-1 d-block" htmlFor={`${idPrefix}_city`}>
                City
              </label>
              <input
                id={`${idPrefix}_city`}
                type="text"
                className="form-control form-control-solid"
                value={pharmacyFilters.city}
                onChange={(e) => handlePharmacyFilterChange('city', e.target.value)}
                placeholder="City"
                autoComplete="off"
              />
            </div>
            <div className="col-12 col-md-4">
              <label className="rxnt-pharmacy-field-label mb-1 d-block" htmlFor={`${idPrefix}_state`}>
                State
              </label>
              <FormInputs
                id={`${idPrefix}_state`}
                type="SelectList"
                options={stateList}
                placeholder="State"
                className="form-control form-control-solid"
                name={`${idPrefix}_state`}
                value={pharmacyFilters.state}
                required={false}
                changeHandler={(value) => handlePharmacyFilterChange('state', value)}
              />
            </div>
            <div className="col-12 col-md-4">
              <label className="rxnt-pharmacy-field-label mb-1 d-block" htmlFor={`${idPrefix}_zip`}>
                ZIP
              </label>
              <FormInputs
                id={`${idPrefix}_zip`}
                type="ZipCode"
                placeholder="ZIP"
                className="form-control form-control-solid"
                name={`${idPrefix}_zip`}
                value={pharmacyFilters.zip}
                required={false}
                max={9}
                changeHandler={(value) => handlePharmacyFilterChange('zip', value)}
              />
            </div>
          </div>
          <div className="row g-3 mt-0">
            <div className="col-12 col-md-6">
              <label className="rxnt-pharmacy-field-label mb-1 d-block" htmlFor={`${idPrefix}_phone`}>
                Phone
              </label>
              <input
                id={`${idPrefix}_phone`}
                type="text"
                inputMode="numeric"
                className="form-control form-control-solid"
                value={pharmacyFilters.phone}
                onChange={(e) => handlePharmacyFilterChange('phone', e.target.value)}
                placeholder="10 digits"
                maxLength={10}
              />
            </div>
            <div className="col-12 col-md-6">
              <label className="rxnt-pharmacy-field-label mb-1 d-block" htmlFor={`${idPrefix}_fax`}>
                Fax
              </label>
              <input
                id={`${idPrefix}_fax`}
                type="text"
                inputMode="numeric"
                className="form-control form-control-solid"
                value={pharmacyFilters.fax}
                onChange={(e) => handlePharmacyFilterChange('fax', e.target.value)}
                placeholder="10 digits"
                maxLength={10}
              />
            </div>
          </div>
          <div className="row mt-3">
            <div className="col-12 d-flex flex-wrap justify-content-center align-items-center gap-2 rxnt-pharmacy-search-actions">
              <button
                type="button"
                className="btn btn-secondary sh_btnsecondary rxnt-pharmacy-search-cta d-inline-flex align-items-center justify-content-center"
                onClick={handlePharmacySearchClear}
              >
                Clear
              </button>
              <button
                type="button"
                className="btn btn-primary sh_btnprimary rxnt-pharmacy-search-cta d-inline-flex align-items-center justify-content-center"
                onClick={runSearch}
                disabled={pharmacySearch.loading}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="me-2 flex-shrink-0"
                  aria-hidden
                >
                  <path
                    d="M21 21l-4.35-4.35M11 18a7 7 0 100-14 7 7 0 000 14z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                Search
              </button>
            </div>
          </div>
        </div>

        <div className="rxnt-search-container rxnt-pharmacy-results-wrap mt-3">
          {showPharmacyDropdown && (
            <div
              className="rxnt-search-dropdown rxnt-search-dropdown--pharmacy"
              onScroll={handlePharmacyDropdownScroll}
              role="listbox"
              aria-label="Pharmacy search results"
            >
              {pharmacySearch.error ? (
                <div className="rxnt-dropdown-loading rxnt-dropdown-error px-3 py-4">
                  {pharmacySearch.error}
                </div>
              ) : pharmacySearch.loading && !pharmacySearch.loadingMore ? (
                <div className="rxnt-dropdown-loading rxnt-pharmacy-dropdown-state px-3 py-4">
                  <span className="rxnt-pharmacy-spinner mr-2" aria-hidden />
                  Searching pharmacies…
                </div>
              ) : pharmacySearch.results.length === 0 ? (
                <div className="rxnt-dropdown-loading rxnt-pharmacy-dropdown-state text-muted px-3 py-4">
                  No pharmacies match these filters. Try a wider search.
                </div>
              ) : (
                <>
                  {pharmacySearch.results.map((pharmacy, idx) => (
                    <button
                      key={pharmacy.pharmacyId || pharmacy.id || idx}
                      type="button"
                      className="rxnt-dropdown-item rxnt-dropdown-item--action text-left w-100 border-0 bg-transparent"
                      onClick={() => handlePharmacySelect(pharmacy)}
                    >
                      <div className="rxnt-dropdown-item-main">
                        {pharmacy.name || pharmacy.pharmacyName}
                      </div>
                      <div className="rxnt-dropdown-item-sub">
                        {formatPharmacyAddress(pharmacy)}
                        {getPharmacyPhone(pharmacy) ? ` · ${getPharmacyPhone(pharmacy)}` : ''}
                      </div>
                    </button>
                  ))}
                  {pharmacySearch.loadingMore && (
                    <div className="rxnt-dropdown-loading py-3">
                      <span className="rxnt-pharmacy-spinner mr-2" aria-hidden />
                      Loading more…
                    </div>
                  )}
                  {pharmacySearch.hasNext && !pharmacySearch.loadingMore && (
                    <div className="rxnt-dropdown-hint">
                      Scroll for more ({pharmacySearch.results.length}
                      {pharmacySearch.totalCount
                        ? ` of ${pharmacySearch.totalCount.toLocaleString()}`
                        : ''}
                      )
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {selectedPharmacy && (
          <div className="rxnt-pharmacy-selected-card mt-3">
            <div className="d-flex align-items-start justify-content-between flex-wrap gap-2">
              <div className="rxnt-pharmacy-selected-body">
                <span className="rxnt-pharmacy-selected-badge">Selected pharmacy</span>
                <div className="rxnt-pharmacy-selected-name font-weight-bold mt-2">
                  {selectedPharmacy.name || selectedPharmacy.pharmacyName}
                </div>
                <div className="rxnt-pharmacy-selected-meta small text-muted mt-1">
                  {formatPharmacyAddress(selectedPharmacy)}
                </div>
                {getPharmacyPhone(selectedPharmacy) && (
                  <div className="rxnt-pharmacy-selected-meta small mt-1">
                    <span className="text-muted">Phone </span>
                    {getPharmacyPhone(selectedPharmacy)}
                  </div>
                )}
              </div>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm sh_btnsecondary rxnt-pharmacy-remove-btn flex-shrink-0"
                onClick={handleRemoveSelected}
              >
                Change
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RxntPharmacyPicker;
